import React, { useCallback, useContext, useEffect } from 'react'
import { useTabState, Tab, TabList, TabPanel } from 'reakit/Tab'
import styled, { ThemeContext } from 'styled-components'
import history from 'shared/utils/history'
import useResizeObserver from 'shared/hooks/useResizeObserver'
import SelectAutocomplete from '../SelectAutocomplete'
import { useAtBreakpoints } from 'shared/hooks/useBreakpoint'
import useRouter from 'shared/hooks/useRouter'

export type TabType = {
  index?: boolean
  key: string
  label: string
  element: React.ReactNode
}

interface TabsProps {
  tabs: TabType[]
  baseRoute?: string
  defaultTab?: string
  onChange?: (key: string) => void
  tabId?: string
  className?: string
  hideTabs?: boolean
}

function Tabs(props: TabsProps) {
  const { tabs, defaultTab, baseRoute, onChange, tabId, className, hideTabs } = props

  const tabState = useTabState({ selectedId: defaultTab })
  const [ref, entry] = useResizeObserver()
  const isLarge = useAtBreakpoints(['tablet', 'tablet-ipad', 'tablet-landscape', 'tablet-ipad-landscape', 'desktop'])
  const showTab = isLarge || tabs.length === 1
  const theme = useContext(ThemeContext)
  const router = useRouter()

  const onTabChange = useCallback(
    (tabId: string | null, isChangeRoute = true) => {
      if (tabId) {
        tabState.setSelectedId(tabId)
        onChange && onChange(tabId)
      }

      if (baseRoute && isChangeRoute) {
        history.push(`${baseRoute}/${tabId}`, router?.location?.state)
      }
    },
    [tabState, baseRoute, history, router, onChange],
  )

  useEffect(() => {
    tabState.setSelectedId(tabId)
    onTabChange(defaultTab || '', false)
  }, [router])

  const selectStyles = {
    control: (styles: Object) => ({
      ...styles,
      backgroundColor: theme.colors.white,
      fontSize: '16px',
      color: theme.colors.main,
      minHeight: '50px',
      borderRadius: theme.borderRadius,
      border: `1px solid ${theme.colors.main}`,
      outline: '0',
      boxShadow: 'none',
      ':hover': {
        border: `1px solid ${theme.colors.main}`,
        boxShadow: `0px 0px 5px 1px ${theme.colors.focusShadow}`,
      },
      active: {
        border: `1px solid ${theme.colors.main}`,
        boxShadow: `0px 0px 5px 1px ${theme.colors.focusShadow}`,
      },
      focus: {
        border: `1px solid ${theme.colors.main}`,
        boxShadow: `0px 0px 5px 1px ${theme.colors.focusShadow}`,
      },
    }),
    option: (styles: Object, { isFocused }: any) => ({
      ...styles,
      color: theme.colors.secondary,
      backgroundColor: isFocused ? theme.colors.tableAlternate : theme.colors.white,
      minHeight: '32px',
      fontSize: '16px',
      fontWeight: 500,
    }),
    singleValue: (styles: Object) => ({
      ...styles,
      color: theme.colors.main,
      fontWeight: 'bold',
    }),
    dropdownIndicator: (styles: Object) => ({
      ...styles,
      color: theme.colors.main,
      ':hover': {
        color: theme.colors.main,
      },
      active: {
        color: theme.colors.main,
      },
      focus: {
        color: theme.colors.main,
      },
    }),
  }

  return (
    <>
      <StyledTabList
        {...tabState}
        $showBottomBorder={isLarge && !hideTabs}
        $tabsLength={tabs.length}
        aria-label="Tabs"
        className={className}
        id={tabId}
      >
        {!isLarge && tabs.length > 1 && (
          <SelectAutocomplete
            error={''}
            value={tabState.selectedId}
            id="tabs"
            options={tabs.map(tab => ({
              label: tab.label,
              value: tab.key,
            }))}
            onChange={val => onTabChange(val)}
            styles={selectStyles}
          />
        )}
        {tabs.map(tab => (
          <StyledTab
            key={tab.key}
            {...tabState}
            id={tab.key}
            select={onTabChange}
            $fontSize={tabs.length > 4 ? '18px' : '20px'}
            $selected={tabState.selectedId === tab.key}
            $single={tabs.length === 1}
            $showTab={showTab && !hideTabs}
            $tabsLength={tabs.length}
            ref={el => (tabState.selectedId === tab.key ? ref(el) : false)}
          >
            {tab.label}
          </StyledTab>
        ))}
        {showTab && (
          <TabIndicator
            width={(entry?.target as HTMLElement)?.offsetWidth}
            left={(entry?.target as HTMLElement)?.offsetLeft}
          />
        )}
      </StyledTabList>
      {tabs.map(tab => (
        <StyledTabPanel key={tab.key} {...tabState}>
          {tabState.selectedId === tab.key && tab.element}
        </StyledTabPanel>
      ))}
    </>
  )
}

const StyledTabList = styled(TabList)<{ $showBottomBorder: boolean; $tabsLength: number }>`
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  grid-column-gap: 2px;
  ${({ theme, $showBottomBorder }) => $showBottomBorder && `border-bottom: 1px solid ${theme.colors.border};`}
  z-index: 15;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }

  ${({ theme }) => theme.mediaQueries.largeOnly} {
    // If there are more than 3 tabs, convert to display: flex so that tabs can be scrollable
    ${({ $tabsLength }) =>
      $tabsLength > 3 &&
      `
        display: flex;
        justify-content: space-between;
        overflow-x: auto;
      `}
  }

  ${({ theme }) => theme.mediaQueries.mediumOnly} {
    // If there are more than 3 tabs, convert to display: flex so that tabs can be scrollable
    ${({ $tabsLength }) =>
      $tabsLength > 3 &&
      `
        display: flex;
        justify-content: space-between;
        overflow-x: auto;
      `}
  }
`

const StyledTab = styled(Tab)<{
  $fontSize: string
  $selected: boolean
  $single: boolean
  $showTab: boolean
  $tabsLength: number
}>`
  background: #ffffff;
  border: 0;
  padding-bottom: 12px;
  height: 40px;
  outline: 0;
  grid-row: 1;
  max-width: calc(100vw - 56px);
  ${({ theme, $selected, $fontSize, $single, $showTab, $tabsLength }) => `
  font-size: ${$fontSize};
    font-weight: ${$selected ? '900' : '500'};
    color: ${$selected ? theme.colors.main : theme.colors.lightGrey};
    cursor: ${$single ? 'default' : 'pointer'};
    display: ${$showTab ? 'block' : 'none'};
    min-width: ${$tabsLength > 3 ? '190px' : '100px'}
  `}
`

const StyledTabPanel = styled(TabPanel)`
  outline: 0;
`

const TabIndicator = styled.div<{ width?: number; left?: number }>`
  position: absolute;
  bottom: 0;
  width: ${({ width }) => `${width ?? 0}px`};
  transform: translateX(${({ left }) => `${left ?? 0}px`});
  transition: transform 250ms ease;
  height: 6px;
  background: ${({ theme }) => theme.colors.main};
`

export default Tabs
